import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFolderAwareDeleteAction } from "../../hooks/useFolderAwareDeleteAction";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import ModalBasicLayout from "./ModalBasicLayout";
import ModalMedia from "./ModalMedia";
import ModalCreateContentFolder from "./ModalCreateContentFolder";
import ModalMoveContentsToFolder from "./ModalMoveContentsToFolder";
import ModalRenameContentFolder from "./ModalRenameContentFolder";
import ModalRenameAsset from "./ModalRenameAsset";
import Gallery from "../Gallery/Gallery";
import GalleryFolderBar from "../Gallery/GalleryFolderBar";
import Button from "../Buttons/Button";
import IconButton from "../Buttons/IconButton";
import UploadFileButton from "../Buttons/UploadFileButton";
import GalleryUploadMenu from "../Gallery/GalleryUploadMenu";
import Loader from "../Loaders/Loader";
import AssetUploadLoader from "../Loaders/AssetUploadLoader";
import TitlePage from "../Texts/TitlePage";
import GallerySelectedCountChip from "../Gallery/GallerySelectedCountChip";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  startAddAssetsToList,
  startDeleteAssetFromListAndStorage,
  startDownloadUrlsAsZip,
  startGetAwsDownloadUrl,
  startGetGalleryAssets,
  startRenameAsset,
} from "../../actions/user";
import { useAssetGalleryFolderSession } from "../../hooks/useAssetGalleryFolderSession";
import { useEnsureGalleryUploadFolder } from "../../hooks/useEnsureGalleryUploadFolder";
import { usePagination } from "../../hooks/usePagination";
import useNearScreen from "../../hooks/useNearScreen";
import { getUniqueID } from "../../utils/numbers";
import { isLikelyImageUrl } from "../../utils/string";
import {
  getGalleryItemSelectionId,
  getGalleryItemsBySelectionIds,
} from "../../utils/gallerySelection";
import { getCommonFolderID } from "../../utils/contentFolderUtils";
import { ConfirmAlert } from "../Alerts/Alerts";
import {
  BUTTON_ADD_SELECTED_CONTENT,
  BUTTON_CLOSE,
  BUTTON_DELETE_CONTENT,
  BUTTON_DOWNLOAD,
  DELETE_MULTIPLE_CONTENT_CONFIRMATION_DESCRIPTION,
  DELETE_MULTIPLE_CONTENT_CONFIRMATION_TITLE,
  GALLERY_ASSETS_TITLE,
  GALLERY_FOLDERS_ALL,
  GALLERY_FOLDERS_MOVE,
  LOADING_CONTENT,
  MODAL_GALLERY_ARIA_LABEL,
  MODAL_GALLERY_EMPTY_CONTENT,
} from "../../i18n/keysTranslations";
import {
  ALL_FILTER,
  EDITOR_ASSETS_LIST_ROOT,
  GALLERY_BULK_INDIVIDUAL_DOWNLOAD_ZIP_THRESHOLD,
  IMAGE_FORMATS,
} from "../../utils/constants";
import { BORDER_GRAY_COLOR } from "../../utils/colors";
import { stripAssetFileExtension } from "../../utils/assetGalleryUtils";
import { getSelectedDirectoryName } from "../../utils/galleryDirectoryUpload";

import { LaunchError } from "../../utils/errors";

const PAGE_SIZE = 20;

const paddings = {
  pl: { xs: 1.5, sm: 5 },
  pr: { xs: 2.5, sm: 6 },
};

const toolbarRowSx = {
  display: "flex",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
  py: 2,
  pr: paddings.pr,
  pl: paddings.pl,
  backgroundColor: "common.white",
  borderBottom: `1px solid ${BORDER_GRAY_COLOR}`,
};

const formatGalleryContentEntriesFromResponse = (response) => {
  if (!response || typeof response !== "object") return [];
  return Object.keys(response)
    .sort(
      (left, right) =>
        (response[right]?.creationTime || 0) -
        (response[left]?.creationTime || 0)
    )
    .map((key) => ({
      ...response[key],
      id: key,
    }));
};

const isSelectableAssetImage = (item) => {
  if (!item?.url) return false;
  if (item.isCarousel || item.type === "carousel") return false;
  if (item.type === "video") return false;
  return isLikelyImageUrl(item.url);
};

const ModalGalleryAssets = ({
  modalOpen,
  multiple = true,
  onCloseModal = () => {},
  onCallbackSelectedItems = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const galleryRef = useRef(null);
  const galleryFetchGenerationRef = useRef(0);
  const getNextMediaTimerRef = useRef(null);
  const lastCallTimeRef = useRef(0);
  const pendingUploadFileNamesRef = useRef([]);
  const pendingUploadFolderIDRef = useRef(null);
  const [uploadInputId] = useState(() => getUniqueID());
  const [folderUploadInputId] = useState(() => getUniqueID());
  const [folderFilterID, setFolderFilterID] = useState(ALL_FILTER);
  const [data, setData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoadingSource, setIsLoadingSource] = useState(false);
  const [isFetchingNewContents, setIsFetchingNewContents] = useState(false);
  const [loadLastItem, setLoadLastItem] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectPreview, setSelectPreview] = useState(null);
  const [renameAssetTarget, setRenameAssetTarget] = useState(null);
  const [isRenamingAsset, setIsRenamingAsset] = useState(false);

  const { rowsPerPage, lastKey, onChangeLastKey } = usePagination({
    rowsPerPageValue: PAGE_SIZE,
    onCallBackNextPage: () => {},
  });
  const { isNearScreen, fromRef } = useNearScreen({
    distance: "400px",
    once: false,
    disabledTimeout: true,
    scrollRootRef: galleryRef,
  });

  const clearSelection = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const onFolderFilterChange = useCallback((nextFolderFilterID) => {
    setFolderFilterID(nextFolderFilterID);
  }, []);

  const {
    folders,
    activeFolderID,
    isLoadingFolders,
    isCreatingFolder,
    isMovingContents,
    isRenamingFolder,
    stampFolderContent,
    onSelectFolder,
    onDropMany,
    moveContents,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    isOpenMoveFolderModal,
    openMoveFolderModal,
    closeMoveFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
    isOpenRenameFolderModal,
    openRenameFolderModal,
    closeRenameFolderModal,
    renameFolderTarget,
    onRenameFolder,
    onDeleteFolder,
  } = useAssetGalleryFolderSession({
    modalOpen,
    folderFilterID,
    onFolderFilterChange,
    setData,
    clearSelection,
  });

  const imageItems = useMemo(() => data.filter(isSelectableAssetImage), [data]);
  const selectedItemsSet = useMemo(
    () => new Set(selectedItems),
    [selectedItems]
  );
  const selectedGalleryItems = useMemo(
    () => getGalleryItemsBySelectionIds(imageItems, selectedItems),
    [imageItems, selectedItems]
  );
  const selectedContentIDs = useMemo(
    () => selectedGalleryItems.map((item) => item?.id).filter(Boolean),
    [selectedGalleryItems]
  );
  const disabledMoveFolderID = useMemo(
    () => getCommonFolderID({ items: selectedGalleryItems }),
    [selectedGalleryItems]
  );
  const currentFolderLabelForMove = useMemo(() => {
    if (activeFolderID) {
      return getFolderLabel({ folderID: activeFolderID });
    }
    return t(GALLERY_FOLDERS_ALL);
  }, [activeFolderID, getFolderLabel, t]);

  const queryFolderID = folderFilterID === ALL_FILTER ? null : folderFilterID;

  const onCancelGetNextMedia = useCallback(() => {
    if (getNextMediaTimerRef.current) {
      clearTimeout(getNextMediaTimerRef.current);
      getNextMediaTimerRef.current = null;
    }
  }, []);

  const getInitialData = useCallback(async () => {
    onCancelGetNextMedia();
    galleryFetchGenerationRef.current += 1;
    const fetchGeneration = galleryFetchGenerationRef.current;

    setIsLoadingSource(true);
    setIsFetchingNewContents(false);
    setLoadLastItem(false);
    setData([]);
    onChangeLastKey(undefined);

    const guardedOnChangeLastKey = (key) => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        onChangeLastKey(key);
      }
    };
    const guardedOnLoadLastItem = () => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        setLoadLastItem(true);
      }
    };

    const response = await dispatch(
      startGetGalleryAssets({
        lastKey: undefined,
        onChangeLastKey: guardedOnChangeLastKey,
        limit: rowsPerPage,
        onLoadLastItem: guardedOnLoadLastItem,
        folderID: queryFolderID,
      })
    );

    if (fetchGeneration !== galleryFetchGenerationRef.current) return;

    if (response) {
      setData(formatGalleryContentEntriesFromResponse(response));
    }

    setIsLoadingSource(false);
  }, [
    dispatch,
    onCancelGetNextMedia,
    onChangeLastKey,
    queryFolderID,
    rowsPerPage,
  ]);

  const getNextMediaImmediate = useCallback(async () => {
    if (isLoadingSource || loadLastItem || isFetchingNewContents) return;

    const fetchGeneration = galleryFetchGenerationRef.current;
    setIsFetchingNewContents(true);

    const guardedOnChangeLastKey = (key) => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        onChangeLastKey(key);
      }
    };
    const guardedOnLoadLastItem = () => {
      if (fetchGeneration === galleryFetchGenerationRef.current) {
        setLoadLastItem(true);
      }
    };

    const response = await dispatch(
      startGetGalleryAssets({
        lastKey,
        onChangeLastKey: guardedOnChangeLastKey,
        limit: rowsPerPage,
        onLoadLastItem: guardedOnLoadLastItem,
        folderID: queryFolderID,
      })
    );

    if (fetchGeneration !== galleryFetchGenerationRef.current) {
      setIsFetchingNewContents(false);
      return;
    }

    if (response) {
      const dataFormatted = formatGalleryContentEntriesFromResponse(response);
      setData((prevState) => {
        const existingIds = new Set(prevState.map((item) => item.id));
        const newItems = dataFormatted.filter(
          (item) => !existingIds.has(item.id)
        );
        return [...prevState, ...newItems];
      });
    }

    setIsFetchingNewContents(false);
  }, [
    dispatch,
    isFetchingNewContents,
    isLoadingSource,
    lastKey,
    loadLastItem,
    onChangeLastKey,
    queryFolderID,
    rowsPerPage,
  ]);

  const scheduleGetNextMedia = useCallback(() => {
    onCancelGetNextMedia();
    getNextMediaTimerRef.current = setTimeout(() => {
      getNextMediaTimerRef.current = null;
      getNextMediaImmediate();
    }, 350);
  }, [getNextMediaImmediate, onCancelGetNextMedia]);

  useEffect(() => {
    if (modalOpen) {
      getInitialData();
    } else {
      setSelectPreview(null);
      setFolderFilterID(ALL_FILTER);
      setRenameAssetTarget(null);
    }
    return () => {
      onCancelGetNextMedia();
    };
    // Same architecture as ModalGallery: reload only when the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderFilterID]);

  useEffect(() => {
    if (
      !isNearScreen ||
      loadLastItem ||
      isFetchingNewContents ||
      isLoadingSource
    ) {
      return undefined;
    }

    const now = Date.now();
    const timeSinceLastCall = now - lastCallTimeRef.current;
    const throttleDelay = 300;

    if (timeSinceLastCall < throttleDelay) {
      const timeoutId = setTimeout(() => {
        lastCallTimeRef.current = Date.now();
        scheduleGetNextMedia();
      }, throttleDelay - timeSinceLastCall);
      return () => clearTimeout(timeoutId);
    }

    lastCallTimeRef.current = now;
    scheduleGetNextMedia();
    return undefined;
  }, [
    isFetchingNewContents,
    isLoadingSource,
    isNearScreen,
    loadLastItem,
    scheduleGetNextMedia,
  ]);

  useEffect(() => {
    setSelectedItems((prev) => {
      if (prev.length === 0) return prev;
      const existingIds = new Set(
        imageItems
          .map((item) => getGalleryItemSelectionId(item))
          .filter(Boolean)
      );
      const next = prev.filter((selectionId) => existingIds.has(selectionId));
      return next.length === prev.length ? prev : next;
    });
  }, [imageItems]);

  const onSelectItem = useCallback(
    (index) => {
      const item = imageItems[index];
      const selectionId = getGalleryItemSelectionId(item);
      if (!selectionId) return;

      setSelectedItems((prev) => {
        const isCurrentlySelected = prev.includes(selectionId);
        if (isCurrentlySelected) {
          return prev.filter((id) => id !== selectionId);
        }
        if (multiple) {
          return [...prev, selectionId];
        }
        return [selectionId];
      });
    },
    [imageItems, multiple]
  );

  const onSelectPreview = useCallback((indexOrData) => {
    if (typeof indexOrData === "object" && indexOrData !== null) {
      setSelectPreview(indexOrData);
      return;
    }
    setSelectPreview(indexOrData);
  }, []);

  const onDeleteItem = useCallback((index) => {
    setData((prevState) => {
      const visible = prevState.filter(isSelectableAssetImage);
      const deletedItem = visible[index];
      const deletedSelectionId = getGalleryItemSelectionId(deletedItem);

      if (deletedSelectionId) {
        setSelectedItems((prevSelected) =>
          prevSelected.filter(
            (selectionId) => selectionId !== deletedSelectionId
          )
        );
        return prevState.filter(
          (item) => getGalleryItemSelectionId(item) !== deletedSelectionId
        );
      }

      if (!deletedItem) return prevState;
      return prevState.filter((item) => item !== deletedItem);
    });
  }, []);

  const onDeleteSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    const result = await ConfirmAlert({
      title: t(DELETE_MULTIPLE_CONTENT_CONFIRMATION_TITLE),
      text: t(DELETE_MULTIPLE_CONTENT_CONFIRMATION_DESCRIPTION, {
        count: selectedItems.length,
      }),
      confirmButtonText: t(BUTTON_DELETE_CONTENT),
    });

    if (!result.isConfirmed) return;

    try {
      const validItems = getGalleryItemsBySelectionIds(
        imageItems,
        selectedItems
      ).filter((item) => item?.id);

      if (validItems.length === 0) return;

      const deletePromises = validItems.map((item) =>
        dispatch(startDeleteAssetFromListAndStorage({ contentID: item.id }))
      );

      await Promise.all(deletePromises);

      const deletedSelectionIds = new Set(
        validItems
          .map((item) => getGalleryItemSelectionId(item))
          .filter(Boolean)
      );

      setData((prevState) =>
        prevState.filter(
          (item) => !deletedSelectionIds.has(getGalleryItemSelectionId(item))
        )
      );
      setSelectedItems([]);
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onDeleteSelectedItems",
          capture: "exception",
        })
      );
      console.error("Error deleting selected assets:", error);
    }
  };

  const onRemoveItemFromFolder = useCallback(
    (item) => {
      if (!item?.id) return;
      moveContents({ contentIDs: [item.id], folderID: null });
    },
    [moveContents]
  );

  const {
    label: deleteSelectedLabel,
    Icon: DeleteSelectedIcon,
    onAction: onDeleteOrRemoveSelectedItems,
  } = useFolderAwareDeleteAction({
    activeFolderID,
    deleteLabel: t(BUTTON_DELETE_CONTENT),
    deleteIcon: DeleteIcon,
    onDelete: onDeleteSelectedItems,
    onRemoveFromFolder: () =>
      moveContents({ contentIDs: selectedContentIDs, folderID: null }),
  });

  const onDownloadSelectedItems = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const selected = getGalleryItemsBySelectionIds(imageItems, selectedItems);
    const urls = selected.map((item) => item?.url).filter(Boolean);
    if (urls.length === 0) return;

    setIsDownloading(true);
    try {
      if (urls.length > GALLERY_BULK_INDIVIDUAL_DOWNLOAD_ZIP_THRESHOLD) {
        await dispatch(
          startDownloadUrlsAsZip({
            urls,
            fileName: "assets.zip",
          })
        );
      } else {
        await dispatch(startGetAwsDownloadUrl(urls));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const ensureUploadFolder = useEnsureGalleryUploadFolder({
    folders,
    parentFolderID: createFolderParentID,
    onCreateFolder: onCreateFolderInContext,
  });

  const rememberUploadFileNames = useCallback((files) => {
    pendingUploadFileNamesRef.current = Array.from(files).map((file) =>
      stripAssetFileExtension({ name: file.name })
    );
  }, []);

  const onFileSelect = useCallback(
    (files) => {
      rememberUploadFileNames(files);
      pendingUploadFolderIDRef.current = null;
    },
    [rememberUploadFileNames]
  );

  const onDirectoryFileSelect = useCallback(
    async (files) => {
      rememberUploadFileNames(files);

      const folderName = getSelectedDirectoryName(files);
      if (!folderName) {
        pendingUploadFolderIDRef.current = null;
        return;
      }

      const folderID = await ensureUploadFolder({ name: folderName });
      if (!folderID) {
        pendingUploadFolderIDRef.current = null;
        return false;
      }

      pendingUploadFolderIDRef.current = folderID;
    },
    [ensureUploadFolder, rememberUploadFileNames]
  );

  const onAddUploadedContent = async (newMedias) => {
    const urls = (Array.isArray(newMedias) ? newMedias : [newMedias]).filter(
      Boolean
    );
    const fileNames = pendingUploadFileNamesRef.current;
    const folderID = pendingUploadFolderIDRef.current;
    pendingUploadFileNamesRef.current = [];
    pendingUploadFolderIDRef.current = null;

    const contents = urls.map((url, index) =>
      stampFolderContent({
        url,
        from: "upload",
        type: "image",
        userID: null,
        name: fileNames[index] || undefined,
        ...(folderID ? { folderID } : {}),
      })
    );
    if (!contents.length) return;

    const saved = await dispatch(startAddAssetsToList(contents));
    const savedItems = saved
      ? formatGalleryContentEntriesFromResponse(saved)
      : contents.map((content, index) => ({
          ...content,
          id: `upload-${Date.now()}-${index}`,
        }));

    setData((prev) => {
      const existingIds = new Set(prev.map((item) => item.id));
      const newItems = savedItems.filter((item) => !existingIds.has(item.id));
      return [...newItems, ...prev];
    });

    if (folderID && folderID !== activeFolderID) {
      onSelectFolder(folderID);
    }
  };

  const onCallbackMoveToFolder = useCallback(
    async ({ folderID }) =>
      moveContents({
        contentIDs: selectedContentIDs,
        folderID,
      }),
    [moveContents, selectedContentIDs]
  );

  const onOpenRenameAssetModal = useCallback((item) => {
    setRenameAssetTarget(item);
  }, []);

  const onCloseRenameAssetModal = useCallback(() => {
    setRenameAssetTarget(null);
  }, []);

  const onCallbackRenameAsset = useCallback(
    async ({ contentID, name }) => {
      setIsRenamingAsset(true);
      try {
        const response = await dispatch(startRenameAsset({ contentID, name }));

        if (response?.ok) {
          setData((prevState) =>
            prevState.map((item) =>
              item.id === contentID
                ? { ...item, name: response.data.name }
                : item
            )
          );
        }

        return response;
      } finally {
        setIsRenamingAsset(false);
      }
    },
    [dispatch]
  );

  const onSendSelectedItems = async () => {
    if (selectedItems.length === 0) return;
    const selected = getGalleryItemsBySelectionIds(imageItems, selectedItems)
      .map((item) => {
        if (!item?.url) return null;
        return {
          url: item.url,
          from: item.from,
          id: item.id,
          type: "image",
          name: item.name,
        };
      })
      .filter(Boolean);
    if (!selected.length) return;
    await onCallbackSelectedItems(selected);
    onCloseModal();
  };

  const previewIndex =
    selectPreview !== null && typeof selectPreview === "object"
      ? selectPreview.index
      : selectPreview;
  const previewItem =
    previewIndex === null || previewIndex === undefined
      ? null
      : imageItems[previewIndex];

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      hasTitle={false}
      ariaLabel={t(MODAL_GALLERY_ARIA_LABEL)}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      sxPaper={{
        height: { xs: "90vh", sm: "80vh" },
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
      }}
      sxContent={{
        px: 0,
        pb: 0,
        mt: 0,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <UploadFileButton
        id={uploadInputId}
        value=""
        onChange={onAddUploadedContent}
        onFileSelect={onFileSelect}
        acceptedFormats={IMAGE_FORMATS}
        multiple
        pathStorage={`${EDITOR_ASSETS_LIST_ROOT}/${userID}/`}
        onCallbackLoading={setIsUploading}
        isHidden
      />
      <UploadFileButton
        id={folderUploadInputId}
        value=""
        directory
        onChange={onAddUploadedContent}
        onFileSelect={onDirectoryFileSelect}
        onUploadSettled={() => {
          pendingUploadFolderIDRef.current = null;
        }}
        acceptedFormats={IMAGE_FORMATS}
        multiple
        pathStorage={`${EDITOR_ASSETS_LIST_ROOT}/${userID}/`}
        onCallbackLoading={setIsUploading}
        isHidden
      />
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          pr: paddings.pr,
          pl: paddings.pl,
          height: 84,
          bgcolor: "background.paper",
        }}
      >
        <TitlePage>{t(GALLERY_ASSETS_TITLE)}</TitlePage>
        <IconButton onClick={onCloseModal} tooltipText={BUTTON_CLOSE}>
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          borderTop: `1px solid ${BORDER_GRAY_COLOR}`,
        }}
      >
        <Box sx={toolbarRowSx}>
          <GalleryUploadMenu
            isUploading={isUploading}
            onUploadFiles={() =>
              document.getElementById(uploadInputId)?.click()
            }
            onUploadFolder={() =>
              document.getElementById(folderUploadInputId)?.click()
            }
            sx={{ width: { xs: "100%", md: "auto" } }}
          />
        </Box>
        <Box
          sx={{
            ...toolbarRowSx,
            alignItems: "flex-start",
            py: 1,
          }}
        >
          <GalleryFolderBar
            folders={folders}
            pinBottomPadding
            activeFolderID={activeFolderID}
            selectedContentIDs={selectedContentIDs}
            canEdit
            isLoading={isLoadingFolders || isCreatingFolder || isMovingContents}
            onSelectFolder={onSelectFolder}
            onOpenCreateFolder={openCreateFolderModal}
            onDropMany={onDropMany}
            onRenameFolder={openRenameFolderModal}
            onDeleteFolder={onDeleteFolder}
          />
        </Box>

        <Box
          ref={galleryRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {isLoadingSource ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Loader size={70} hasMessage message={t(LOADING_CONTENT)} />
            </Box>
          ) : (
            <>
              <Gallery
                data={imageItems}
                lastItemRef={fromRef}
                loadLastItem={loadLastItem}
                onSelectItem={onSelectItem}
                onSelectPreview={onSelectPreview}
                onDeleteItem={onDeleteItem}
                onRemoveItemFromFolder={
                  activeFolderID ? onRemoveItemFromFolder : null
                }
                selectedItems={selectedItemsSet}
                selectedItemsArray={selectedItems}
                selectionBy="id"
                multiple={multiple}
                overlayPickerMode
                listRoot={EDITOR_ASSETS_LIST_ROOT}
                showActionButtons
                showDownloadButton
                showDeleteButton
                showPostButton={false}
                showEditButton={false}
                showDimensionsOverlay
                showEmptyMessage={false}
                showAssetName
                onRenameAsset={onOpenRenameAssetModal}
                actionStripMinHeight={42}
                acceptedFormats={IMAGE_FORMATS}
                columns={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}
                sxGallery={{
                  pl: paddings.pl,
                  pr: paddings.pr,
                  pt: 2,
                }}
                draggableForFolders
                selectedItemsCount={selectedItems.length}
              />
              {!isUploading && imageItems.length === 0 && loadLastItem ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                  }}
                >
                  <Typography variant="h6" align="center">
                    {t(MODAL_GALLERY_EMPTY_CONTENT)}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            bgcolor: "background.paper",
            borderTop: `1px solid ${BORDER_GRAY_COLOR}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            pl: paddings.pl,
            pr: paddings.pr,
            py: 1.5,
          }}
        >
          <GallerySelectedCountChip
            selectedCount={selectedItems.length}
            sx={{ mt: 0 }}
          />
          <Button
            id="modal-gallery-assets-download-selected"
            color="secondary"
            iconOnly
            ariaLabel={t(BUTTON_DOWNLOAD)}
            tooltip={t(BUTTON_DOWNLOAD)}
            disabled={selectedItems.length === 0}
            loading={isDownloading}
            startIcon={<DownloadIcon sx={{ fontSize: 24 }} />}
            onClick={onDownloadSelectedItems}
          />
          <Button
            id="modal-gallery-assets-move-to-folder-selected"
            color="secondary"
            iconOnly
            ariaLabel={t(GALLERY_FOLDERS_MOVE)}
            tooltip={t(GALLERY_FOLDERS_MOVE)}
            disabled={selectedItems.length === 0 || isMovingContents}
            startIcon={<DriveFileMoveIcon sx={{ fontSize: 24 }} />}
            onClick={(event) => {
              event?.preventDefault();
              event?.stopPropagation();
              openMoveFolderModal();
            }}
          />
          <Button
            id="modal-gallery-assets-delete-selected"
            color="secondary"
            iconOnly
            ariaLabel={deleteSelectedLabel}
            tooltip={deleteSelectedLabel}
            disabled={selectedItems.length === 0}
            startIcon={<DeleteSelectedIcon sx={{ fontSize: 24 }} />}
            onClick={(event) => {
              event?.preventDefault();
              event?.stopPropagation();
              onDeleteOrRemoveSelectedItems();
            }}
          />
          <Button
            color="primary"
            disabled={selectedItems.length === 0}
            onClick={onSendSelectedItems}
          >
            {t(BUTTON_ADD_SELECTED_CONTENT)}
          </Button>
        </Box>
      </Box>
      <AssetUploadLoader open={isUploading} />
      {previewItem ? (
        <ModalMedia
          modalOpen
          onCloseModal={() => setSelectPreview(null)}
          url={previewItem.url}
          item={previewItem}
          listRoot={EDITOR_ASSETS_LIST_ROOT}
          showEditButton={false}
          showShareButton={false}
          showDeleteButton
          showDownloadButton
          onDeleteSuccess={() => onDeleteItem(previewIndex)}
        />
      ) : null}
      <ModalCreateContentFolder
        modalOpen={isOpenCreateFolderModal}
        folders={folders}
        parentFolderID={createFolderParentID}
        parentFolderLabel={createFolderParentLabel}
        isLoading={isCreatingFolder}
        onCloseModal={closeCreateFolderModal}
        onCallbackCreate={onCreateFolderInContext}
      />
      <ModalMoveContentsToFolder
        modalOpen={isOpenMoveFolderModal}
        folders={folders}
        currentFolderLabel={currentFolderLabelForMove}
        createParentFolderID={createFolderParentID}
        createParentFolderLabel={createFolderParentLabel}
        disabledFolderID={disabledMoveFolderID}
        isLoading={isMovingContents}
        isCreatingFolder={isCreatingFolder}
        onCloseModal={closeMoveFolderModal}
        onCallbackCreate={onCreateFolderInContext}
        onCallbackMove={onCallbackMoveToFolder}
      />
      <ModalRenameContentFolder
        modalOpen={isOpenRenameFolderModal}
        folders={folders}
        folder={renameFolderTarget}
        isLoading={isRenamingFolder}
        onCloseModal={closeRenameFolderModal}
        onCallbackRename={onRenameFolder}
      />
      <ModalRenameAsset
        modalOpen={Boolean(renameAssetTarget)}
        asset={renameAssetTarget}
        isLoading={isRenamingAsset}
        onCloseModal={onCloseRenameAssetModal}
        onCallbackRename={onCallbackRenameAsset}
      />
    </ModalBasicLayout>
  );
};

export default ModalGalleryAssets;
